import { Autocomplete, CircularProgress, Grid, InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { CellRulePanel, CellRuleStudyId } from 'interfaces/cellRule';
import { find, isEmpty } from 'lodash';
import React from 'react';
import useUsersById from 'utils/queryHooks/useUsersById';

interface CellRulePanelDropdownProps {
  panels: CellRulePanel[];
  selectedPanelId: string;
  onSelectPanel: (panelId: string) => void;
  isLoading: boolean;
  studyId: CellRuleStudyId;
  textFieldProps?: TextFieldProps;
}

const emptyPanel: Partial<CellRulePanel> = { id: '', label: '' };

const CellRulePanelDropdown: React.FC<CellRulePanelDropdownProps> = ({
  panels,
  selectedPanelId,
  onSelectPanel,
  isLoading,
  studyId,
  textFieldProps,
}) => {
  const approvedPanel = find(panels, (p) => !isEmpty(p.approvedAt));
  const { getUserNameById } = useUsersById({ enabled: Boolean(approvedPanel) });
  const currentPanel = find(panels, (panel) => panel?.id === selectedPanelId);

  return (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs>
        <Autocomplete
          options={panels}
          getOptionLabel={(panel) => {
            let label = panel?.label ?? 'Unlabelled';
            if (panel?.approvedBy) {
              label += ` (approved by: ${getUserNameById(panel?.approvedBy)})`;
            }
            return label;
          }}
          value={currentPanel ?? emptyPanel}
          onChange={(event, newValue) => {
            const selectedValue = newValue?.id ?? emptyPanel.id;
            onSelectPanel(selectedValue);
          }}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText="No panels available"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select Panel"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <InputAdornment position="end">
                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </InputAdornment>
                ),
              }}
              {...textFieldProps}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default CellRulePanelDropdown;
